import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWallet, faPaperPlane, faAddressCard, faQrcode, faDollarSign, faCheckCircle,
  faExclamationTriangle, faTimesCircle, faInfoCircle, faGamepad, faBurn, faGift,
  faSpinner, faBalanceScale, faHandHoldingHeart, faHandsHelping, faLink
} from '@fortawesome/free-solid-svg-icons';
import { GiBullHorns } from 'react-icons/gi';
import QrScanner from 'react-qr-scanner';
import { useTranslation } from 'react-i18next';

import { getCurrentUser } from '../../services/authService';
import { sendTransaction, checkUTXOs, getBalance as getGlobalBalance } from '../../services/walletService';
import { getGameFundBalance } from '../../services/swapFundsService';

import styles from './Send.module.css';

const EXPLORER_ADDR = 'https://explorer.menelik.cc/address/';
const EXPLORER_TX   = 'https://explorer.menelik.cc/txid/';

const BalanceDisplay = React.memo(({ availableBalance, totalNegativeAmount, t }) => {
  const isValidTotal = totalNegativeAmount > 0 && totalNegativeAmount <= availableBalance;
  return (
    <div className={styles.balanceWrap}>
      <div className={styles.balanceRow}>
        <FontAwesomeIcon icon={faWallet} className={`${styles.balanceIcon} ${styles.iconGold}`} />
        <h4 className={styles.balanceText}>
          {t('available_balance_send', { balance: availableBalance.toFixed(4) })}
        </h4>
      </div>

      <div
        className={`${styles.balanceRow} ${
          totalNegativeAmount > availableBalance ? styles.textDanger : styles.textInfo
        } ${isValidTotal ? styles.pulseGold : ''}`}
      >
        <GiBullHorns className={`${styles.bullIcon} ${styles.iconGold}`} />
        <h5 className={styles.balanceSubText}>
          {t('total_selected', { amount: totalNegativeAmount.toFixed(4) })}
        </h5>
      </div>

      {totalNegativeAmount > availableBalance && (
        <p className={styles.inlineWarn}>
          <FontAwesomeIcon icon={faExclamationTriangle} /> {t('warning_insufficient_funds')}
        </p>
      )}
    </div>
  );
});

const SendTransaction = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Form
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');

  // Contribuir / Donar
  const [contribute, setContribute] = useState(false);
  const [donate, setDonate] = useState(false);
  const [donationAmount, setDonationAmount] = useState('1000');

  // Game Fund
  const [gameFund, setGameFund] = useState(false);
  const [withdrawGameFund, setWithdrawGameFund] = useState(false);

  // UI
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [loading, setLoading] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [transactionData, setTransactionData] = useState(null);

  // Saldos / UTXOs
  const [availableBalance, setAvailableBalance] = useState(0);
  const [totalNegativeAmount, setTotalNegativeAmount] = useState(0);
  const [utxosAvailable, setUtxosAvailable] = useState(false);
  const [checkingUtxos, setCheckingUtxos] = useState(false);

  const intervalRef = useRef(null);

  const updateBalance = useCallback(async () => {
    try {
      if (withdrawGameFund) {
        const gf = await getGameFundBalance();
        setAvailableBalance(gf.availableBalance || 0);
      } else {
        const global = await getGlobalBalance();
        setAvailableBalance(global.availableBalance || 0);
      }
    } catch (error) {
      console.error('Error al obtener saldo:', error);
    }
  }, [withdrawGameFund]);

  const checkUserUTXOs = useCallback(async () => {
    try {
      const response = await checkUTXOs();
      if (response && response.utxos && response.utxos.length > 0) {
        setUtxosAvailable(true);
        await updateBalance();
      } else {
        setUtxosAvailable(false);
      }
    } catch (error) {
      console.error('Error al verificar UTXOs:', error);
      setMessage(error.response?.data?.error || t('error_message'));
      setMessageType('danger');
    }
  }, [updateBalance, t]);

  const startUtxoCheckCycle = useCallback(() => {
    const checkDuration = 4 * 60 * 1000;
    const checkInterval = 30 * 1000;
    const startTime = Date.now();

    setCheckingUtxos(true);
    checkUserUTXOs();

    intervalRef.current = setInterval(() => {
      if (Date.now() - startTime >= checkDuration || utxosAvailable) {
        clearInterval(intervalRef.current);
        setCheckingUtxos(false);
      } else {
        checkUserUTXOs();
      }
    }, checkInterval);

    return () => clearInterval(intervalRef.current);
  }, [checkUserUTXOs, utxosAvailable]);

  useEffect(() => {
    const user = getCurrentUser();
    if (!user) {
      navigate('/login');
    } else {
      updateBalance();
      startUtxoCheckCycle();
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [navigate, updateBalance, startUtxoCheckCycle]);

  useEffect(() => {
    let total = parseFloat(amount) || 0;
    if (contribute) total += 3000;
    if (donate) total += parseInt(donationAmount) || 0;
    setTotalNegativeAmount(total);
  }, [contribute, donate, donationAmount, amount]);

  useEffect(() => {
    if (contribute || donate) {
      setGameFund(false);
      setWithdrawGameFund(false);
    }
  }, [contribute, donate]);

  useEffect(() => {
    if (gameFund || withdrawGameFund) {
      setContribute(false);
      setDonate(false);
    }
  }, [gameFund, withdrawGameFund]);

  const handleSend = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const payload = {
        amount: parseFloat(amount) || 0,
        recipient: recipient ? recipient : '',
        contribute,
        donate,
        donationAmount: parseInt(donationAmount) || 0
      };

      if (withdrawGameFund) {
        payload.withdrawGameFund = true;
        payload.gameFund = false;
        payload.recipient = '';
      } else if (gameFund) {
        payload.gameFund = true;
      }

      const response = await sendTransaction(payload);
      setMessage(t('success_message', { txid: response.txid }));
      setMessageType('success');
      setTransactionData(response);
      await updateBalance();
    } catch (error) {
      console.error(error);
      setMessage(error.response?.data?.error || t('error_message'));
      setMessageType('danger');
    } finally {
      setLoading(false);
    }
  };

  const handleScan = (data) => {
    if (data) {
      setRecipient(data.text);
      setShowQrScanner(false);
    }
  };
  const handleError = (err) => {
    console.error(err);
    alert('Error al leer el código QR');
    setShowQrScanner(false);
  };

  const getIconForAlias = (alias) => {
    switch (alias) {
      case 'Game Fund':
        return <FontAwesomeIcon icon={faGamepad} className={`${styles.icon} ${styles.iconInfoTone}`} />;
      case 'Burn Address':
        return <FontAwesomeIcon icon={faBurn} className={`${styles.icon} ${styles.iconDangerTone}`} />;
      case 'Rewards Address':
        return <FontAwesomeIcon icon={faGift} className={`${styles.icon} ${styles.iconSuccessTone}`} />;
      case 'Ganador':
        return <FontAwesomeIcon icon={faCheckCircle} className={`${styles.icon} ${styles.iconSuccessTone}`} />;
      case 'Change':
      case 'game_fund (change)':
        return <FontAwesomeIcon icon={faWallet} className={`${styles.icon} ${styles.iconWarnTone}`} />;
      default:
        return <FontAwesomeIcon icon={faInfoCircle} className={`${styles.icon} ${styles.iconMutedTone}`} />;
    }
  };

  const getAlertIcon = () => {
    switch (messageType) {
      case 'success':
        return <FontAwesomeIcon icon={faCheckCircle} />;
      case 'danger':
        return <FontAwesomeIcon icon={faTimesCircle} />;
      case 'warning':
        return <FontAwesomeIcon icon={faExclamationTriangle} />;
      default:
        return <FontAwesomeIcon icon={faInfoCircle} />;
    }
  };

  // Vista de detalles con enlaces al explorer
  const renderTransactionDetails = () => {
    if (!transactionData) return null;
    return (
      <div className={styles.txWrap}>
        <h3 className={styles.txTitle}>{t('transaction_details')}</h3>

        <div className={styles.txCard}>
          <div className={styles.txCardBody}>
            <h5 className={styles.txCardTitle}>
              <FontAwesomeIcon icon={faCheckCircle} className={styles.successIcon} />
              <span className={styles.labelGold}>{t('txid_send', { txid: '' })}</span>
              <a
                href={`${EXPLORER_TX}${transactionData.txid}`}
                target="_blank"
                rel="noreferrer"
                className={styles.txidLink}
                title="Ver en el explorer"
              >
                <FontAwesomeIcon icon={faLink} className={styles.txidIcon} />
                <span className={styles.txidText}>{transactionData.txid}</span>
              </a>
            </h5>

            <p className={styles.txMuted}>{t('involved_addresses')}</p>

            <div className={styles.grid}>
              {transactionData.involvedAddresses.map((addr, idx) => (
                <div className={styles.gridItem} key={idx}>
                  <div className={styles.subCard}>
                    <div className={styles.subCardBody}>
                      <h5 className={styles.subCardTitle}>
                        {getIconForAlias(addr.alias)}{' '}
                        <span className={styles.aliasText}>
                          {t(addr.alias.toLowerCase().replace(' ', '_'))}
                        </span>
                      </h5>

                      <p className={styles.subCardText}>
                        <strong className={styles.labelGold}>{t('address', { address: '' })}</strong>
                        <a
                          className={styles.addrLink}
                          href={`${EXPLORER_ADDR}${addr.address}`}
                          target="_blank"
                          rel="noreferrer"
                          title="Ver dirección en el explorer"
                        >
                          <FontAwesomeIcon icon={faLink} className={styles.txidIcon} />
                          <span className={styles.addrText}>{addr.address}</span>
                        </a>
                        <br />
                        <strong className={styles.labelGold}>
                          {t('amount_send', { amount: '' })}
                        </strong>
                        <span className={styles.amountChip}>
                          <GiBullHorns className={styles.bullIconSmall} /> {addr.amount} MENEL
                        </span>
                      </p>

                      <p className={styles.subCardMuted}>
                        {t(`${addr.alias.toLowerCase().replace(' ', '_')}_explanation`)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.title}>
          <span className={styles.titleIconWrap}>
            <FontAwesomeIcon icon={faPaperPlane} className={styles.iconGold} />
          </span>
          {t('send_menelik')}
        </h1>

        <BalanceDisplay
          availableBalance={availableBalance}
          totalNegativeAmount={totalNegativeAmount}
          t={t}
        />

        {checkingUtxos && (
          <div className={`${styles.alert} ${styles.alertInfo}`}>
            <FontAwesomeIcon icon={faSpinner} className={styles.spin} />
            <span className={styles.alertText}>{t('searching_utxos')}</span>
          </div>
        )}

        <form onSubmit={handleSend} className={styles.form}>
          {/* Destinatario */}
          <div className={styles.formGroup}>
            <label htmlFor="recipient" className={styles.label}>
              <FontAwesomeIcon icon={faAddressCard} className={styles.iconGold} /> {t('recipient_address')}
            </label>
            <div className={styles.inputRow}>
              <input
                type="text"
                className={styles.input}
                id="recipient"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder={t('enter_recipient_address')}
                disabled={withdrawGameFund}
              />
              <button
                type="button"
                className={styles.btnGhost}
                onClick={() => setShowQrScanner(!showQrScanner)}
                disabled={withdrawGameFund}
                aria-label="Scan QR"
                title="Scan QR"
              >
                <FontAwesomeIcon icon={faQrcode} />
              </button>
            </div>

            {showQrScanner && (
              <div className={styles.qrWrap}>
                <QrScanner delay={300} onError={handleError} onScan={handleScan} style={{ width: '100%' }} />
              </div>
            )}
          </div>

          {/* Contribuir / Donar */}
          {!(gameFund || withdrawGameFund) && (
            <>
              <div className={styles.checkRow}>
                <label className={styles.checkLabel}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={contribute}
                    onChange={(e) => setContribute(e.target.checked)}
                  />
                  <span className={styles.checkText}>
                    <FontAwesomeIcon icon={faHandsHelping} className={styles.iconGoldSoft} /> {t('contribute_to_ecosystem')}
                  </span>
                </label>
              </div>

              <div className={styles.checkRow}>
                <label className={styles.checkLabel}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={donate}
                    onChange={(e) => setDonate(e.target.checked)}
                  />
                  <span className={styles.checkText}>
                    <FontAwesomeIcon icon={faHandHoldingHeart} className={styles.iconGoldSoft} /> {t('donate_to_team')}
                  </span>
                </label>
              </div>

              {donate && (
                <div className={styles.formGroup}>
                  <label htmlFor="donationAmount" className={styles.label}>
                    {t('select_donation_amount')}
                  </label>
                  <select
                    className={styles.select}
                    id="donationAmount"
                    value={donationAmount}
                    onChange={(e) => setDonationAmount(e.target.value)}
                  >
                    <option value="1000">1000 MENEL</option>
                    <option value="5000">5000 MENEL</option>
                    <option value="10000">10000 MENEL</option>
                  </select>
                </div>
              )}
            </>
          )}

          {/* Game Fund */}
          {!(contribute || donate) && (
            <>
              <div className={styles.checkRow}>
                <label className={styles.checkLabel}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={gameFund && !withdrawGameFund}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setGameFund(true);
                        setWithdrawGameFund(false);
                      } else {
                        setGameFund(false);
                      }
                    }}
                  />
                  <span className={styles.checkText}>
                    <FontAwesomeIcon icon={faGamepad} className={styles.iconInfoTone} /> {t('send_to_game_fund')}
                  </span>
                </label>
              </div>

              <div className={styles.checkRow}>
                <label className={styles.checkLabel}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={withdrawGameFund && !gameFund}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setWithdrawGameFund(true);
                        setGameFund(false);
                      } else {
                        setWithdrawGameFund(false);
                      }
                    }}
                  />
                  <span className={styles.checkText}>
                    <FontAwesomeIcon icon={faGamepad} className={styles.iconWarnTone} /> Withdraw from my gaming fund
                  </span>
                </label>
              </div>
            </>
          )}

          {/* Monto + Enviar */}
          <div className={styles.actionRow}>
            <div className={styles.inputRow}>
              <span className={styles.inputPrefix}>
                <FontAwesomeIcon icon={faDollarSign} /> MENEL
              </span>
              <input
                type="number"
                className={styles.input}
                id="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={t('amount_to_send')}
              />
            </div>

            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={loading || totalNegativeAmount > availableBalance || !utxosAvailable}
            >
              {loading ? t('sending') : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} /> {t('send')}
                </>
              )}
            </button>
          </div>
        </form>

        {message && (
          <div
            className={`${styles.alert} ${
              messageType === 'success'
                ? styles.alertSuccess
                : messageType === 'danger'
                ? styles.alertDanger
                : styles.alertInfo
            }`}
            role="alert"
          >
            {getAlertIcon()} <span className={styles.alertText}>{message}</span>
          </div>
        )}

        {transactionData && renderTransactionDetails()}
      </div>
    </div>
  );
};

export default SendTransaction;
